/**
 * Branch Dashboard - Branch admin main dashboard
 * Follows UX/UI patterns from ClubDashboard with branch-specific content
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';
import apiService from '../../services/api';
import { Card, CardHeader, CardTitle, CardContent, AdminPageLayout, EmptyState, Section } from '../../components';
import { getNavigationIcon } from '../../components/navigation/NavigationIcons';
import StatCard from '../../components/StatCard';
import StatGrid from '../../components/StatGrid';
import { Building, Calendar, Settings, Users, Trophy, Zap } from 'lucide-react';

const BranchDashboard = () => {
  const { branch_id } = useParams();
  const [branchData, setBranchData] = useState(null);
  const [stats, setStats] = useState({
    total_programs: 0,
    total_teams: 0,
    total_players: 0,
    active_coaches: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (branch_id) {
      loadDashboardData();
      loadInboxTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch_id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch branch data
      const branch = await apiService.getBranch(branch_id);
      setBranchData(branch);

      // Fetch branch stats
      try {
        const statsResponse = await apiService.getBranchStats(branch_id);
        setStats({
          total_programs: statsResponse.total_programs || 0,
          total_teams: statsResponse.total_teams || 0,
          total_players: statsResponse.total_players || 0,
          active_coaches: statsResponse.active_coaches || 0
        });
      } catch (statsErr) {
        console.error('Error loading stats:', statsErr);
        // Fall back to zeros if stats fail
        setStats({
          total_programs: 0,
          total_teams: 0,
          total_players: 0,
          active_coaches: 0
        });
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Load pending tasks for branch admin
  const loadInboxTasks = async () => {
    try {
      setTasksLoading(true);
      const response = await apiService.getInboxTasks();
      setTasks(response.tasks?.filter(task => task.role === 'branch_admin') || []);
    } catch (err) {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="BRANCH DASHBOARD"
        subtitle="Loading branch information..."
        backButton={false}
      >
        <div className="loading">Loading dashboard...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout
        title="BRANCH DASHBOARD"
        subtitle="Error loading branch"
        backButton={false}
      >
        <div className="error-message">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <Button onClick={loadDashboardData}>
            Retry
          </Button>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title={`${branchData?.name || 'BRANCH'} DASHBOARD`}
      subtitle={`${branchData?.address?.city || ''} ${branchData?.address?.state || ''} - Overview of branch operations`}
      backButton={false}
      maxWidth="1400px"
    >
      {branchData && (
        <Card level={1} style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Branch</div>
              <div style={{ fontWeight: '600' }}>{branchData.name}</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Status</div>
              <div style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '600',
                background: branchData.status === 'active' ? 'var(--color-success-background)' : 'var(--color-warning-background)',
                color: branchData.status === 'active' ? 'var(--color-success)' : 'var(--color-warning)',
                textTransform: 'capitalize'
              }}>
                {branchData.status}
              </div>
            </div>
            {branchData.address && (
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Location</div>
                <div style={{ fontWeight: '600' }}>
                  {branchData.address.city}, {branchData.address.state}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Pending Actions */}
      {tasks.length > 0 && (
        <Section title="PENDING ACTIONS">
          <Card level={1}>
            {tasksLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading pending actions...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {tasks.map(task => (
                  <Card key={task.id} level={2}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{task.title}</div>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{task.description}</div>
                      </div>
                      <Button onClick={() => navigate(task.link)} size="sm">
                        {task.actionLabel || 'VIEW'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </Section>
      )}

      {/* Statistics Overview */}
      <Section title="STATISTICS">
        <StatGrid>
          <StatCard
            number={stats.total_programs}
            label="Programs"
            detail="Branch programs"
          />
          <StatCard
            number={stats.total_teams}
            label="Teams"
            detail="Active teams"
          />
          <StatCard
            number={stats.total_players}
            label="Players"
            detail="Registered players"
          />
          <StatCard
            number={stats.active_coaches}
            label="Coaches"
            detail="Active coaches"
          />
        </StatGrid>
      </Section>

      {/* Branch Information */}
      {branchData && (
        <Section title="BRANCH INFORMATION">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            <Card level={1}>
              <CardHeader>
                <CardTitle as="h3" style={{ fontSize: '1rem', fontWeight: '600' }}>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {branchData.email && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Email</div>
                      <a href={`mailto:${branchData.email}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
                        {branchData.email}
                      </a>
                    </div>
                  )}
                  {branchData.phone && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Phone</div>
                      <a href={`tel:${branchData.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
                        {branchData.phone}
                      </a>
                    </div>
                  )}
                  {branchData.address && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Address</div>
                      <div style={{ lineHeight: '1.5' }}>
                        {branchData.address.street}<br />
                        {branchData.address.city}, {branchData.address.state} {branchData.address.postal_code}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card level={1}>
              <CardHeader>
                <CardTitle as="h3" style={{ fontSize: '1rem', fontWeight: '600' }}>Facility Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {branchData.facility_type && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Facility Type</div>
                      <div style={{ textTransform: 'capitalize' }}>{branchData.facility_type}</div>
                    </div>
                  )}
                  {branchData.capacity && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Capacity</div>
                      <div>{branchData.capacity} players</div>
                    </div>
                  )}
                  {branchData.operating_hours && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Operating Hours</div>
                      <div>{branchData.operating_hours}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </Section>
      )}

      {/* Quick Actions */}
      <Section title="QUICK ACTIONS">
        <Card level={1}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem'
          }}>
            {[
              { to: `/branch/${branch_id}/programs`, icon: <Trophy size={32} />, title: 'MANAGE PROGRAMS', description: 'View club-level programs' },
              { to: `/branch/${branch_id}/teams`, icon: <Users size={32} />, title: 'MANAGE TEAMS', description: 'Team rosters and assignments' },
              { to: `/branch/${branch_id}/calendars`, icon: <Calendar size={32} />, title: 'CALENDAR MANAGEMENT', description: 'Manage branch calendars and events' },
              { to: `/branch/${branch_id}/templates`, icon: <Zap size={32} />, title: 'TRAINING TEMPLATES', description: 'Manage session and activity templates' },
              { to: `/branch/${branch_id}/users`, icon: <Users size={32} />, title: 'USER MANAGEMENT', description: 'Manage branch users and roles' },
              { to: `/admin/branches/${branch_id}/edit`, icon: <Settings size={32} />, title: 'BRANCH SETTINGS', description: 'Update branch information' }
            ].map((action, idx) => (
              <Link
                key={idx}
                to={action.to}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <Card
                  level={2}
                  style={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{
                      color: 'var(--color-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '64px',
                      height: '64px'
                    }}>
                      {action.icon}
                    </div>
                    <div>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        marginBottom: '0.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {action.title}
                      </div>
                      <div style={{
                        color: 'var(--color-text-secondary)',
                        fontSize: '0.9rem'
                      }}>
                        {action.description}
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

export default BranchDashboard;
